import React from 'react';
import {SetRequired} from 'type-fest';
import {type FieldValues, type FieldPath} from 'react-hook-form';
import {splitEventHandler} from '@netx/core-next/utils';
import {useConstCallback} from '@netx/core-components/hooks/useEvent';
import {combineRefs} from '@netx/core-components/util';
import {
	type SelectFieldProps,
	SelectField,
} from '@netx/core-components/SelectField';
import {
	ReactHookFormControl,
	type WithReactHookFormControlFieldProps,
} from '../ReactHookFormControl';

export interface ReactHookFormSelectFieldProps<
	T,
	Multiple extends boolean = false,
	DisableClearable extends boolean = false,
	FreeSolo extends boolean = false,
	TFieldValues extends FieldValues = FieldValues,
	TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> extends WithReactHookFormControlFieldProps<
		SetRequired<
			SelectFieldProps<T, Multiple, DisableClearable, FreeSolo>,
			'name'
		>,
		TFieldValues,
		TName
	> {}

export const ReactHookFormSelectFieldPrimitive = <
	T,
	Multiple extends boolean = false,
	DisableClearable extends boolean = false,
	FreeSolo extends boolean = false,
	TFieldValues extends FieldValues = FieldValues,
	TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>(
	{
		control,
		ControllerProps,
		...props
	}: ReactHookFormSelectFieldProps<
		T,
		Multiple,
		DisableClearable,
		FreeSolo,
		TFieldValues,
		TName
	>,
	ref: React.ForwardedRef<HTMLDivElement>,
) => {
	return (
		<ReactHookFormControl<TFieldValues, TName>
			{...ControllerProps}
			control={control}
			name={props.name as TName}
			render={({field, formState}) => {
				return (
					<SelectField<T, Multiple, DisableClearable, FreeSolo>
						error={formState.errors[props.name]?.message?.toString()}
						{...props}
						{...field}
						disabled={props.disabled || field.disabled}
						ref={ref}
						inputRef={combineRefs(props.inputRef, field.ref)}
						value={(field.value ?? (props.multiple ? [] : '')) as any}
						onChange={useConstCallback(
							splitEventHandler(
								props.onChange,
								// this is not a classic form change event
								// it comes from an option click within autocomplete
								(event, value) => field.onChange(value),
							),
						)}
					/>
				);
			}}
			rules={{
				required: props.required,
				...ControllerProps?.rules,
			}}
		/>
	);
};

/**
 * `react-hook-form` controlled `SelectField` component
 */
export const ReactHookFormSelectField = React.forwardRef(
	ReactHookFormSelectFieldPrimitive,
) as <
	T,
	Multiple extends boolean = false,
	DisableClearable extends boolean = false,
	FreeSolo extends boolean = false,
	TFieldValues extends FieldValues = FieldValues,
	TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>(
	props: ReactHookFormSelectFieldProps<
		T,
		Multiple,
		DisableClearable,
		FreeSolo,
		TFieldValues,
		TName
	>,
) => JSX.Element;

export default ReactHookFormSelectField;
